import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/ApiService';
import type { SpectatorTicketType } from '../../types/event';
import Button from '../ui/Button';

interface EventSpectatorTicketsPanelProps {
  eventId: number;
  eventSlug?: string | null;
  ticketsEnabled: boolean;
}

function formatMoney(cents: number, currency: string): string {
  const amount = (Math.max(0, cents) / 100).toFixed(2);
  return `${currency.toUpperCase()} $${amount}`;
}

const EventSpectatorTicketsPanel: React.FC<EventSpectatorTicketsPanelProps> = ({
  eventId,
  eventSlug,
  ticketsEnabled,
}) => {
  const [types, setTypes] = useState<SpectatorTicketType[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [name, setName] = useState('General admission');
  const [description, setDescription] = useState('');
  const [priceDollars, setPriceDollars] = useState('25.00');
  const [inventory, setInventory] = useState('');
  const [perOrderLimit, setPerOrderLimit] = useState('10');

  const load = useCallback(async () => {
    if (!ticketsEnabled) {
      setTypes([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setTypes(await apiService.listTicketTypes(eventId));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load ticket types');
      setTypes([]);
    } finally {
      setLoading(false);
    }
  }, [eventId, ticketsEnabled]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticketsEnabled) return;

    const dollars = Number(priceDollars);
    if (!Number.isFinite(dollars) || dollars < 0) {
      setError('Enter a valid price');
      return;
    }
    const limit = Math.max(1, Math.floor(Number(perOrderLimit) || 1));
    const inventoryTrim = inventory.trim();
    const inventoryTotal =
      inventoryTrim === '' ? null : Math.max(0, Math.floor(Number(inventoryTrim)));
    if (inventoryTrim !== '' && !Number.isFinite(Number(inventoryTrim))) {
      setError('Inventory must be a number, or leave blank for unlimited');
      return;
    }

    try {
      setBusy(true);
      setError(null);
      setSuccess(null);
      await apiService.createTicketType({
        event_id: eventId,
        name: name.trim() || 'Spectator',
        description: description.trim() || undefined,
        price_cents: Math.round(dollars * 100),
        currency: 'usd',
        inventory_total: inventoryTotal,
        per_order_limit: limit,
      });
      setSuccess('Ticket type created. Buyers can purchase it from the public event page.');
      setDescription('');
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create ticket type');
    } finally {
      setBusy(false);
    }
  };

  if (!ticketsEnabled) {
    return (
      <div className="form-section space-y-3">
        <h2 className="heading-section text-sm">Spectator tickets</h2>
        <p className="text-sm text-gray-400 leading-relaxed">
          Spectator tickets are not enabled on this platform yet. A platform admin must turn on{' '}
          <span className="text-gray-300">spectator_tickets_enabled</span> (Admin → Payments), and
          your payout setup must be ready before buyers can check out.
        </p>
        <Link to="/organizer/payments" className="text-orange-300 text-sm min-h-11 inline-flex items-center">
          Check payout setup →
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="form-section space-y-3">
        <h2 className="heading-section text-sm">Spectator tickets</h2>
        <p className="text-sm text-gray-400 leading-relaxed">
          Create admission ticket types for non-vehicle attendees. Buyers purchase from the public
          event page; vehicle entrants still register separately.
        </p>
        {eventSlug ? (
          <Link
            to={`/events/${eventSlug}/tickets`}
            className="text-orange-300 text-sm min-h-11 inline-flex items-center"
          >
            Open buyer ticket page →
          </Link>
        ) : null}
      </div>

      <form onSubmit={handleCreate} className="form-section">
        <h3 className="text-sm font-semibold text-white">Create ticket type</h3>

        <div>
          <label htmlFor="ticket-name" className="input-label">
            Name
          </label>
          <input
            id="ticket-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            required
            className="input"
          />
        </div>

        <div>
          <label htmlFor="ticket-desc" className="input-label">
            Description (optional)
          </label>
          <input
            id="ticket-desc"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            className="input"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label htmlFor="ticket-price" className="input-label">
              Price (USD)
            </label>
            <input
              id="ticket-price"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={priceDollars}
              onChange={(e) => setPriceDollars(e.target.value)}
              required
              className="input"
            />
          </div>
          <div>
            <label htmlFor="ticket-inventory" className="input-label">
              Inventory (blank = unlimited)
            </label>
            <input
              id="ticket-inventory"
              type="number"
              inputMode="numeric"
              min="0"
              step="1"
              value={inventory}
              onChange={(e) => setInventory(e.target.value)}
              placeholder="Unlimited"
              className="input"
            />
          </div>
          <div>
            <label htmlFor="ticket-limit" className="input-label">
              Per-order limit
            </label>
            <input
              id="ticket-limit"
              type="number"
              inputMode="numeric"
              min="1"
              step="1"
              value={perOrderLimit}
              onChange={(e) => setPerOrderLimit(e.target.value)}
              required
              className="input"
            />
          </div>
        </div>

        {error ? (
          <p className="text-sm text-red-400" role="alert">
            {error}
          </p>
        ) : null}
        {success ? (
          <p className="text-sm text-emerald-400" role="status">
            {success}
          </p>
        ) : null}

        <Button type="submit" variant="primary" loading={busy} className="min-h-[44px]">
          Create ticket type
        </Button>
      </form>

      <div className="form-section space-y-3">
        <h3 className="text-sm font-semibold text-white">On sale</h3>
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : types.length === 0 ? (
          <p className="text-sm text-gray-500">
            No ticket types yet. Create one above so the public Buy tickets page has something to sell.
          </p>
        ) : (
          <ul className="divide-y divide-white/[0.06]">
            {types.map((t) => (
              <li key={t.id} className="py-3 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="text-sm text-white font-medium">{t.name}</p>
                  <p className="text-sm text-gray-300 tabular-nums">
                    {formatMoney(t.price_cents, t.currency || 'usd')}
                  </p>
                </div>
                {t.description ? <p className="text-xs text-gray-500 mt-1">{t.description}</p> : null}
                <p className="text-xs text-gray-500 mt-1">
                  Sold {t.inventory_sold}
                  {t.remaining === null ? ' · Unlimited inventory' : ` · ${t.remaining} remaining`}
                  {` · Max ${t.per_order_limit} per order`}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default EventSpectatorTicketsPanel;
